import React, { useState, useEffect, useCallback } from 'react'
import { SidebarNavItem } from './SidebarNavItem'
import { SidebarNavGroup, type NavGroupItem } from './SidebarNavGroup'
import { useLocation, Link } from 'react-router-dom'
import { clientConfig } from '@/config'
import type { SidebarIconName } from '../icons/SidebarIcons'
import ricaldLogo from '@/assets/ricald-logo.png'

// Single top-level items (no group)
interface NavItem {
  label: string
  to: string
  icon: SidebarIconName
}

// ============================================================================
// LAUNCH SIDEBAR — 5 core items + gated non-wedge features
// Core wedge: Dashboard, Prospects (Queue + All Prospects), TAG, Alerts, Settings
// Everything else is gated behind feature flags (default OFF).
// ============================================================================

const { features } = clientConfig

// ============================================================================
// PhD Panel Sidebar (5 items) — Today, Accounts, Prospects, Ask Ricald, Activity
// Queue, Alerts, Briefings merged into Today view.
// History, Campaigns, Call Library merged into Activity group.
// Routes still exist — just not in sidebar (Today links to them).
// ============================================================================

// Top-level standalone items
const topLevelItems: NavItem[] = [
  { label: 'Today', to: 'dashboard', icon: 'dashboard' },
  { label: 'Work in motion', to: 'operations', icon: 'sparkles' },
  { label: 'Activity', to: 'activity', icon: 'zap' },
  { label: 'Inbox', to: 'inbox', icon: 'inbox' },
  // Discovery is an Accounts state, not a separate product destination.
  { label: 'Accounts', to: 'accounts', icon: 'globe' },
  { label: 'Prospects', to: 'prospects', icon: 'prospects' },
  ...(features.enableNativeCommercialMode
    ? [{ label: 'Pipeline', to: 'deals', icon: 'briefcase' } as const]
    : []),
  { label: 'Ask Ricald', to: 'query', icon: 'chat' },
  { label: 'Calendar', to: 'calendar', icon: 'calendar' },
]

// Outreach group — unified outreach history + campaigns + conversation. (Labelled
// "Outreach" in the nav; "Activity" is the separate top-level transparency hub.)
const outreachGroup: NavGroupItem[] = [
  { label: 'History', to: 'outreach', icon: 'outreach' },
  ...(features.enableCampaigns
    ? [{ label: 'Campaigns', to: 'campaigns', icon: 'sequences' } as const]
    : []),
  ...(features.enableConversationIntelligence
    ? [{ label: 'Call Library', to: 'conversation/calls', icon: 'mic' } as const]
    : []),
]

// Gated groups — only visible when feature flags are enabled
const adsGroup: NavGroupItem[] = [
  { label: 'Dashboard', to: 'ads', icon: 'megaphone' },
  ...(features.enableAds
    ? [{ label: 'A/B Testing', to: 'ads/ab-testing', icon: 'flask' } as const]
    : []),
]

const signalsGroup: NavGroupItem[] = [
  { label: 'Website Visitors', to: 'website-visitors', icon: 'globe' },
]

const performanceGroup: NavGroupItem[] = [
  ...(features.enableAnalytics
    ? [{ label: 'Analytics', to: 'analytics', icon: 'analytics' } as const]
    : []),
  ...(features.enableAttribution
    ? [{ label: 'Attribution', to: 'attribution', icon: 'dollar-sign' } as const]
    : []),
  ...(features.enableEconomicAdditionality
    ? [{ label: 'Economic Impact', to: 'economic-additionality', icon: 'trending-up' } as const]
    : []),
]

const networkGroup: NavGroupItem[] = [
  { label: 'Agent Profile', to: 'network/profile', icon: 'memory' },
  { label: 'Broadcasting', to: 'network/broadcast', icon: 'megaphone' },
  { label: 'Directory', to: 'network/directory', icon: 'globe' },
]

const utilityItems: NavItem[] = [
  { label: 'Integrations', to: 'settings?tab=integrations', icon: 'plug' },
  { label: 'Settings', to: 'settings', icon: 'settings' },
]

export const Sidebar: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  )
  const location = useLocation()

  // Close drawer on route change
  useEffect(() => { setOpen(false) }, [location.pathname])

  // Escape key closes drawer
  const onKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false)
  }, [])
  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', onKey)
      return () => window.removeEventListener('keydown', onKey)
    }
  }, [open, onKey])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    <>
      {/* Mobile toggle button (visible below md) */}
      <button
        type="button"
        aria-label="Open navigation"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="md:hidden fixed top-3 left-3 z-40 inline-flex items-center justify-center w-10 h-10 rounded-md bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-primary)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
      >
        <span className="sr-only">Open navigation</span>
        <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
      </button>

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          role="presentation"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Drawer + desktop sidebar - Obsidian Elegance */}
      <aside
        className={`flex flex-col fixed md:relative z-50 md:z-auto top-0 left-0 h-full md:h-auto transition-transform md:transition-none md:translate-x-0 md:flex w-64 shrink-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}
        style={{
          background: 'linear-gradient(180deg, var(--bg-card) 0%, var(--bg-canvas) 100%)',
          borderRight: '1px solid var(--border)',
        }}
        aria-hidden={isMobile && !open}
      >
        {/* Gold accent line at top */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: 'linear-gradient(90deg, var(--accent), transparent 80%)' }}
          aria-hidden
        />
        <div className="h-16 flex items-center px-6 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            <Link to="/app/dashboard">
              <img src={ricaldLogo} alt={clientConfig.branding.productName} style={{ height: '28px', width: 'auto' }} />
            </Link>
          </div>
          {/* Close button (mobile) */}
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setOpen(false)}
            className="md:hidden ml-auto inline-flex items-center justify-center w-8 h-8 rounded-md text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto" aria-label="Primary navigation">
          {/* 5-item sidebar: Today, Accounts, Prospects, Ask Ricald */}
          {topLevelItems.map(item => (
            <SidebarNavItem key={item.to} to={item.to} label={item.label} icon={item.icon} />
          ))}

          {/* Outreach — unified outreach history + campaigns + calls. Named "Outreach"
              (not "Activity") so it doesn't collide with the top-level Activity hub link. */}
          <div className="pt-2">
            <SidebarNavGroup
              label="Outreach"
              items={outreachGroup}
              storageKey="outreach"
              defaultExpanded={true}
            />
          </div>

          <SidebarNavGroup
            label="Signals"
            items={signalsGroup}
            storageKey="signals"
            defaultExpanded={true}
          />

          <SidebarNavGroup
            label="Ads"
            items={adsGroup}
            storageKey="ads"
            defaultExpanded={false}
          />

          {/* Gated: Performance (Analytics, Attribution, Economic Impact) */}
          {performanceGroup.length > 0 && (
            <SidebarNavGroup
              label="Performance"
              items={performanceGroup}
              storageKey="performance"
              defaultExpanded={false}
            />
          )}

          {/* Gated: Marketplace */}
          {(features.enableNetwork || features.enableMarketplace) && (
            <SidebarNavGroup
              label="Marketplace"
              items={networkGroup}
              storageKey="network"
              defaultExpanded={false}
            />
          )}
        </nav>
        <div className="px-2 pb-4 space-y-1 border-t" style={{ borderColor: 'var(--border)' }} aria-label="Utilities">
          {utilityItems.map(item => (
            <SidebarNavItem key={item.to} to={item.to} label={item.label} icon={item.icon} />
          ))}
          {/* Removed "Try on sample data" — confuses customers */}
        </div>
      </aside>
    </>
  )
}
